import { useEffect, useRef, useState } from "react";
import { Check, PencilSimple, Plus, Trash, X } from "@phosphor-icons/react";
import type { ObservedSpeaker, SessionGenerationConfig, SessionMeta, SessionPanelist } from "../../shared/types.ts";
import { useDirtySyncedState } from "../lib/useDirtySyncedState.ts";
import {
  PanelistAliasPicker,
  UnassignedSpeakerQueue,
  type AssignSpeakerAliasInput,
  type CreatePanelistForSpeakerInput,
  type ReplacePanelistAliasesInput,
} from "./SpeakerAliasControls.tsx";

export interface PanelInfoSavePatch {
  purpose?: string;
  themes?: string[];
}

export interface PanelistSaveInput {
  originalName?: string;
  panelist: SessionPanelist;
}

/**
 * Left-rail "Panel" card: session title (read-only here — renamed via
 * SessionControl in the topbar, which calls sessions.updateTitle, a
 * different mutation than the one this card writes to), synopsis
 * (click-to-edit -> generation.purpose), themes (removable chips ->
 * generation.themes, plus an add affordance), and panelists (name+bio rows ->
 * generation.panelists, feeding suggestion + recap prompts). The caller's
 * onSave is expected to persist synopsis/themes through the generation config;
 * roster writes use dedicated callbacks so speaker mappings stay synchronized.
 */
export function PanelInfoCard({
  meta,
  generation,
  onSave,
  onSavePanelist,
  onRemovePanelist,
  speakers,
  onAssignSpeakerAlias,
  onReplacePanelistAliases,
}: {
  meta: SessionMeta;
  generation: SessionGenerationConfig;
  onSave: (patch: PanelInfoSavePatch) => void;
  onSavePanelist: (input: PanelistSaveInput) => Promise<void>;
  onRemovePanelist: (name: string) => Promise<void>;
  speakers: ObservedSpeaker[];
  onAssignSpeakerAlias: (input: AssignSpeakerAliasInput) => Promise<void>;
  onReplacePanelistAliases: (input: ReplacePanelistAliasesInput) => Promise<void>;
}) {
  const [editingSynopsis, setEditingSynopsis] = useState(false);
  const [synopsisDraft, setSynopsisDraft] = useState(generation.purpose);
  const [addingTheme, setAddingTheme] = useState(false);
  const [themeDraft, setThemeDraft] = useState("");
  const [editingPanelists, setEditingPanelists] = useState<Set<number>>(() => new Set());
  const [panelistErrors, setPanelistErrors] = useState<Record<number, string>>({});
  const [busyPanelists, setBusyPanelists] = useState<Set<number>>(() => new Set());
  const [speakerPanelistDraft, setSpeakerPanelistDraft] = useState<
    (CreatePanelistForSpeakerInput & SessionPanelist & { busy: boolean; error: string | null }) | null
  >(null);
  const synopsisRef = useRef<HTMLTextAreaElement>(null);
  const {
    draft: panelistsDraft,
    setDraft: setPanelistsDraft,
    commitBaseline: commitPanelists,
  } = useDirtySyncedState({
    value: generation.panelists,
    resetKey: meta.id,
    isEqual: panelistsEqual,
  });

  // Session switched: drop any in-progress edit and load the new session's data.
  useEffect(() => {
    setEditingSynopsis(false);
    setSynopsisDraft(generation.purpose);
    setAddingTheme(false);
    setThemeDraft("");
    setEditingPanelists(new Set());
    setPanelistErrors({});
    setBusyPanelists(new Set());
    setSpeakerPanelistDraft(null);
    // Only re-run on an actual session switch — see the effect below for
    // picking up live purpose updates while this card isn't mid-edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meta.id]);

  // Live updates from elsewhere (e.g. another admin tab) while not editing.
  useEffect(() => {
    if (!editingSynopsis) setSynopsisDraft(generation.purpose);
  }, [generation.purpose, editingSynopsis]);

  function startEditingSynopsis() {
    setEditingSynopsis(true);
    requestAnimationFrame(() => synopsisRef.current?.focus());
  }

  function commitSynopsis() {
    setEditingSynopsis(false);
    const trimmed = synopsisDraft.trim();
    if (trimmed !== generation.purpose) onSave({ purpose: trimmed });
  }

  function cancelSynopsis() {
    setSynopsisDraft(generation.purpose);
    setEditingSynopsis(false);
  }

  function removeTheme(index: number) {
    onSave({ themes: generation.themes.filter((_, i) => i !== index) });
  }

  function commitAddTheme() {
    const trimmed = themeDraft.trim();
    setThemeDraft("");
    setAddingTheme(false);
    if (!trimmed || generation.themes.includes(trimmed)) return;
    onSave({ themes: [...generation.themes, trimmed] });
  }

  function updatePanelist(index: number, patch: Partial<SessionPanelist>) {
    setPanelistsDraft((current) =>
      current.map((panelist, i) => (i === index ? { ...panelist, ...patch } : panelist)),
    );
  }

  function addPanelist() {
    const nextIndex = panelistsDraft.length;
    setPanelistsDraft((current) => [...current, { name: "", bio: "" }]);
    setEditingPanelists(new Set([nextIndex]));
  }

  function createPanelistForSpeaker(input: CreatePanelistForSpeakerInput) {
    setSpeakerPanelistDraft({
      ...input,
      name: input.suggestedName,
      bio: "",
      busy: false,
      error: null,
    });
  }

  async function saveSpeakerPanelistDraft() {
    if (!speakerPanelistDraft || speakerPanelistDraft.busy) return;
    const name = speakerPanelistDraft.name.trim();
    const bio = speakerPanelistDraft.bio.trim();
    if (!name) {
      setSpeakerPanelistDraft((current) => current ? { ...current, error: "Panelist name is required." } : null);
      return;
    }
    setSpeakerPanelistDraft((current) => current ? { ...current, busy: true, error: null } : null);
    try {
      await onAssignSpeakerAlias({ speakerKey: speakerPanelistDraft.speakerKey, name, bio });
      setSpeakerPanelistDraft(null);
    } catch (error) {
      setSpeakerPanelistDraft((current) => current ? {
        ...current,
        busy: false,
        error: errorMessage(error),
      } : null);
    }
  }

  async function removePanelist(index: number) {
    const panelist = panelistsDraft[index];
    if (!panelist?.name.trim()) return;
    setPanelistBusy(index, true);
    clearPanelistError(index);
    try {
      await onRemovePanelist(panelist.name);
      const next = panelistsDraft.filter((_, i) => i !== index);
      commitPanelists(next);
      setEditingPanelists((current) => shiftPanelistIndexesAfterRemoval(current, index));
      setPanelistErrors({});
    } catch (error) {
      setPanelistError(index, errorMessage(error));
    } finally {
      setPanelistBusy(index, false);
    }
  }

  function editPanelist(index: number) {
    setEditingPanelists((current) => new Set(current).add(index));
  }

  function cancelPanelistEdit(index: number) {
    const persisted = generation.panelists[index];
    if (persisted) {
      setPanelistsDraft((current) =>
        current.map((panelist, i) => (i === index ? persisted : panelist)),
      );
      setEditingPanelists((current) => dropPanelistIndex(current, index));
    } else {
      setPanelistsDraft((current) => current.filter((_, i) => i !== index));
      setEditingPanelists((current) => shiftPanelistIndexesAfterRemoval(current, index));
    }
  }

  async function savePanelist(index: number) {
    const panelist = normalizePanelist(panelistsDraft[index] ?? { name: "", bio: "" });
    if (!panelist.name) {
      setPanelistError(index, "Panelist name is required.");
      return;
    }
    setPanelistBusy(index, true);
    clearPanelistError(index);
    try {
      await onSavePanelist({
        originalName: generation.panelists[index]?.name,
        panelist,
      });
      const next = panelistsDraft.map((candidate, i) => i === index ? panelist : candidate);
      commitPanelists(next);
      setEditingPanelists((current) => dropPanelistIndex(current, index));
    } catch (error) {
      setPanelistError(index, errorMessage(error));
    } finally {
      setPanelistBusy(index, false);
    }
  }

  async function toggleModerator(index: number) {
    const panelist = panelistsDraft[index];
    if (!panelist?.name.trim()) return;
    setPanelistBusy(index, true);
    clearPanelistError(index);
    try {
      const next = { ...normalizePanelist(panelist), isModerator: !panelist.isModerator };
      await onSavePanelist({ originalName: generation.panelists[index]?.name, panelist: next });
      // The server clears sibling flags (single moderator); mirror that locally
      // so the roster reads correctly before the next state round-trip.
      commitPanelists(panelistsDraft.map((candidate, i) => {
        if (i === index) return next;
        return next.isModerator && candidate.isModerator
          ? { ...candidate, isModerator: false }
          : candidate;
      }));
    } catch (error) {
      setPanelistError(index, errorMessage(error));
    } finally {
      setPanelistBusy(index, false);
    }
  }

  function setPanelistBusy(index: number, busy: boolean) {
    setBusyPanelists((current) => {
      const next = new Set(current);
      if (busy) next.add(index);
      else next.delete(index);
      return next;
    });
  }

  function setPanelistError(index: number, message: string) {
    setPanelistErrors((current) => ({ ...current, [index]: message }));
  }

  function clearPanelistError(index: number) {
    setPanelistErrors((current) => {
      if (!(index in current)) return current;
      const next = { ...current };
      delete next[index];
      return next;
    });
  }

  return (
    <section className="panel admin2-panel-info" data-testid="admin2-panel-info">
      <div className="panel-head">
        <h2>Panel</h2>
        <button type="button" className="edit-btn" onClick={startEditingSynopsis} title="Edit synopsis">
          <PencilSimple /> edit
        </button>
      </div>

      <div className="info-label">Title</div>
      <div className="info-val title">{meta.title}</div>

      <div className="info-label">Synopsis</div>
      <div className="info-val edit" data-testid="admin2-panel-synopsis">
        {editingSynopsis ? (
          <textarea
            ref={synopsisRef}
            value={synopsisDraft}
            onChange={(e) => setSynopsisDraft(e.target.value)}
            onBlur={commitSynopsis}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); commitSynopsis(); }
              if (e.key === "Escape") cancelSynopsis();
            }}
          />
        ) : (
          <span
            role="button"
            tabIndex={0}
            onClick={startEditingSynopsis}
            onKeyDown={(e) => { if (e.key === "Enter") startEditingSynopsis(); }}
          >
            {generation.purpose || "Add a synopsis…"}
          </span>
        )}
      </div>

      <div className="info-label">Themes</div>
      <div className="theme-chips">
        {generation.themes.map((theme, index) => (
          // Index keys/removal: pre-existing duplicate themes must not break
          // reconciliation or delete both copies at once.
          <span className="theme-chip" key={index}>
            {theme}
            <button
              type="button"
              className="theme-chip-remove"
              aria-label={`Remove theme ${theme}`}
              onClick={() => removeTheme(index)}
            >
              <X weight="bold" />
            </button>
          </span>
        ))}
        {addingTheme ? (
          <input
            className="theme-chip-input"
            autoFocus
            value={themeDraft}
            onChange={(e) => setThemeDraft(e.target.value)}
            onBlur={commitAddTheme}
            onKeyDown={(e) => {
              if (e.key === "Enter") { e.preventDefault(); commitAddTheme(); }
              if (e.key === "Escape") { setThemeDraft(""); setAddingTheme(false); }
            }}
            placeholder="New theme…"
          />
        ) : (
          <button type="button" className="theme-chip add" aria-label="Add theme" onClick={() => setAddingTheme(true)}>
            <Plus weight="bold" />
          </button>
        )}
      </div>

      <div className="info-label">Panelists</div>
      <UnassignedSpeakerQueue
        speakers={speakers}
        panelists={generation.panelists}
        onAssignAlias={onAssignSpeakerAlias}
        onCreatePanelist={createPanelistForSpeaker}
      />
      {speakerPanelistDraft && (
        <div className="generation-panelist-row editing speaker-assignment-draft" data-testid="speaker-panelist-inline-draft">
          <div className="speaker-assignment-context">
            Assigning <span className="speaker-alias-pill unassigned">{speakerPanelistDraft.speakerLabel}</span>
          </div>
          <input
            autoFocus
            data-testid="speaker-alias-create-name"
            value={speakerPanelistDraft.name}
            placeholder="Panelist name"
            aria-label="Panelist name"
            disabled={speakerPanelistDraft.busy}
            onChange={(event) => setSpeakerPanelistDraft((current) => current ? {
              ...current,
              name: event.target.value,
              error: null,
            } : null)}
            onKeyDown={(event) => {
              if (event.key === "Enter") { event.preventDefault(); void saveSpeakerPanelistDraft(); }
              if (event.key === "Escape") setSpeakerPanelistDraft(null);
            }}
          />
          <textarea
            data-testid="speaker-alias-create-bio"
            value={speakerPanelistDraft.bio}
            placeholder="Role / bio — Shift+Enter for a new line"
            aria-label="Role or bio"
            rows={2}
            disabled={speakerPanelistDraft.busy}
            onChange={(event) => setSpeakerPanelistDraft((current) => current ? {
              ...current,
              bio: event.target.value,
              error: null,
            } : null)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault();
                void saveSpeakerPanelistDraft();
              }
              if (event.key === "Escape") setSpeakerPanelistDraft(null);
            }}
          />
          <div className="panelist-row-actions">
            <button
              type="button"
              className="icon-btn confirm"
              title="Create and assign panelist"
              aria-label="Create and assign panelist"
              data-testid="speaker-alias-create-save"
              disabled={speakerPanelistDraft.busy}
              onClick={() => void saveSpeakerPanelistDraft()}
            >
              <Check weight="bold" />
            </button>
            <button
              type="button"
              className="icon-btn cancel"
              title="Cancel panelist creation"
              aria-label="Cancel panelist creation"
              disabled={speakerPanelistDraft.busy}
              onClick={() => setSpeakerPanelistDraft(null)}
            >
              <X weight="bold" />
            </button>
          </div>
          {speakerPanelistDraft.error && (
            <div className="panelist-row-error" role="alert">{speakerPanelistDraft.error}</div>
          )}
        </div>
      )}
      {panelistsDraft.map((panelist, index) => {
        const editing = editingPanelists.has(index) || !isConfirmedPanelist(panelist);
        const busy = busyPanelists.has(index);
        const canonicalPanelist = generation.panelists.find(
          (candidate) => panelistNameKey(candidate.name) === panelistNameKey(panelist.name),
        );
        return (
          <div className={`generation-panelist-row ${editing ? "editing" : "display"}`} key={index}>
            {editing ? (
              <>
                <input
                  data-testid="generation-panelist-name"
                  value={panelist.name}
                  placeholder="Name"
                  onChange={(e) => updatePanelist(index, { name: e.target.value })}
                  disabled={busy}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") { e.preventDefault(); savePanelist(index); }
                    if (e.key === "Escape") cancelPanelistEdit(index);
                  }}
                />
                <textarea
                  data-testid="generation-panelist-bio"
                  value={panelist.bio}
                  placeholder="Bio / role — Shift+Enter for a new line"
                  rows={2}
                  onChange={(e) => updatePanelist(index, { bio: e.target.value })}
                  disabled={busy}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); savePanelist(index); }
                    if (e.key === "Escape") cancelPanelistEdit(index);
                  }}
                />
                <div className="panelist-row-actions">
                  <button
                    type="button"
                    className="icon-btn confirm"
                    title="Save panelist"
                    aria-label="Save panelist"
                    data-testid="generation-panelist-save"
                    disabled={busy}
                    onClick={() => void savePanelist(index)}
                  >
                    <Check weight="bold" />
                  </button>
                  <button
                    type="button"
                    className="icon-btn cancel"
                    title="Cancel panelist edit"
                    aria-label="Cancel panelist edit"
                    data-testid="generation-panelist-cancel"
                    disabled={busy}
                    onClick={() => cancelPanelistEdit(index)}
                  >
                    <X weight="bold" />
                  </button>
                </div>
                {panelistErrors[index] && (
                  <div className="panelist-row-error" role="alert" data-testid="generation-panelist-error">
                    {panelistErrors[index]}
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="panelist-display" data-testid="generation-panelist-display">
                  <div className="panelist-display-name">
                    <span className="panelist-name-text" data-testid="generation-panelist-name-display">
                      {panelist.name || "Unnamed panelist"}
                    </span>
                    <button
                      type="button"
                      className={`modtag-toggle${panelist.isModerator ? " is-moderator" : ""}`}
                      data-testid="generation-panelist-moderator-toggle"
                      title={panelist.isModerator ? "Unset moderator" : "Make moderator"}
                      aria-pressed={Boolean(panelist.isModerator)}
                      disabled={busy}
                      onClick={() => void toggleModerator(index)}
                    >
                      moderator
                    </button>
                  </div>
                  {panelist.bio && (
                    <div className="panelist-display-bio" data-testid="generation-panelist-bio-display">
                      {panelist.bio}
                    </div>
                  )}
                  {canonicalPanelist && (
                    <PanelistAliasPicker
                      key={panelistNameKey(canonicalPanelist.name)}
                      panelist={canonicalPanelist}
                      speakers={speakers}
                      onReplacePanelistAliases={onReplacePanelistAliases}
                    />
                  )}
                </div>
                <div className="panelist-row-actions">
                  <button
                    type="button"
                    className="icon-btn"
                    title="Edit panelist"
                    aria-label="Edit panelist"
                    data-testid="generation-panelist-edit"
                    disabled={busy}
                    onClick={() => editPanelist(index)}
                  >
                    <PencilSimple weight="bold" />
                  </button>
                  <button
                    type="button"
                    className="icon-btn"
                    title="Remove panelist"
                    aria-label="Remove panelist"
                    data-testid="generation-panelist-remove"
                    disabled={busy}
                    onClick={() => void removePanelist(index)}
                  >
                    <Trash weight="bold" />
                  </button>
                </div>
              </>
            )}
          </div>
        );
      })}
      <button
        type="button"
        className="btn ghost sm panelist-add-btn"
        data-testid="generation-panelist-add"
        onClick={addPanelist}
      >
        <Plus weight="bold" /> Add panelist
      </button>
    </section>
  );
}

function normalizePanelist(panelist: SessionPanelist): SessionPanelist {
  return {
    name: panelist.name.trim(),
    bio: panelist.bio.trim(),
    ...(panelist.isModerator ? { isModerator: true } : {}),
  };
}

function panelistNameKey(name: string): string {
  return name.trim().toLowerCase();
}

function isConfirmedPanelist(panelist: SessionPanelist): boolean {
  return Boolean(panelist.name.trim() || panelist.bio.trim());
}

function dropPanelistIndex(indexes: Set<number>, indexToDrop: number): Set<number> {
  const next = new Set(indexes);
  next.delete(indexToDrop);
  return next;
}

function shiftPanelistIndexesAfterRemoval(indexes: Set<number>, removedIndex: number): Set<number> {
  const next = new Set<number>();
  indexes.forEach((index) => {
    if (index < removedIndex) next.add(index);
    if (index > removedIndex) next.add(index - 1);
  });
  return next;
}

function panelistsEqual(a: SessionPanelist[], b: SessionPanelist[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((panelist, index) =>
    panelist.name === b[index]?.name &&
    panelist.bio === b[index]?.bio &&
    Boolean(panelist.isModerator) === Boolean(b[index]?.isModerator),
  );
}

function errorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  return "Could not update panelist.";
}
